"use client";

export interface CraftView {
  target: { slug: string; name: string; count: number } | null;
  class: string;
  materials: { id: string; name: string; count: number }[];
  tools: { id: string; name: string }[];
  total: number;
  at: number;
  question: { stem: string; choices: string[] } | null;
  picked: { stem: string; text: string; feel: string }[];
  result: { no: number; key: string; name: string; factor: number; taboo: boolean; coefficient: number; score: number; pass: number; base: number; lines: number[] } | null;
}

/** 数。デモだけが渡す */
export interface CraftNumbers {
  stat: number;
  factor: number;
  bonus: number;
  base: number;
  pass: number;
  lines: number[];
  coefficient: number;
  values: string[];
}

const STAGE_NAMES = ["ゴミ", "粗悪", "ちょっと悪い", "合格", "良品", "極み"];

function resultLine(view: CraftView): string {
  const r = view.result;
  if (!r) return "";
  if (r.taboo) return "やってはならない手を打った。素材は駄目になった。";
  if (r.factor === 0) return "形にならなかった。素材は駄目になった。";
  return `${view.target?.name ?? "品"}ができた。`;
}

/**
 * 作製のクイズ。問いを 1 つずつ出し、答えを受ける。終わったら出来と、打った手を並べる。
 * 進行の一時画面と作製デモプレイが共に使う
 */
export default function CraftQuiz({
  view,
  feel,
  busy,
  numbers,
  onAnswer,
}: {
  view: CraftView;
  /** 直前の答えの手応え */
  feel: string;
  busy: boolean;
  numbers?: CraftNumbers | null;
  onAnswer: (index: number) => void;
}) {
  const names = (rows: { name: string; count?: number }[]) => rows.map((one) => (one.count && one.count > 1 ? `${one.name} ${one.count}` : one.name)).join("・") || "無し";

  return (
    <>
      <div className="cf-card">
        <div className="cf-kv">
          <span>作る物</span>
          <b>
            {view.target?.name ?? "—"}
            {view.target && view.target.count > 1 ? ` ${view.target.count}` : ""}
          </b>
        </div>
        <div className="cf-kv">
          <span>素材</span>
          <b>{names(view.materials)}</b>
        </div>
        <div className="cf-kv">
          <span>道具</span>
          <b>{names(view.tools)}</b>
        </div>
        {numbers ? (
          <p className="cf-num">
            初期値 <b>{numbers.base}</b>（実数値 {numbers.stat} × 係数 {numbers.factor} ＋ 加算 {numbers.bonus}）／合格点 <b>{numbers.pass}</b>（{view.class}）／今の係数 <b>{numbers.coefficient}</b>
            <br />
            線 {numbers.lines.map((line, index) => `${STAGE_NAMES[index + 1]} ${line}`).join("・")}
          </p>
        ) : null}
      </div>

      {view.result ? (
        <>
          <div className={view.result.factor === 0 ? "cf-card cf-result cf-result-junk" : "cf-card cf-result"}>
            <small>出来</small>
            <b>{view.result.name}</b>
            <p>{resultLine(view)}</p>
            {numbers ? (
              <p className="cf-num">
                結果 <b>{view.result.score}</b> ＝ 初期値 {view.result.base} × 係数 {view.result.coefficient}
              </p>
            ) : null}
          </div>
          <div className="cf-card">
            <ul className="cf-trail">
              {view.picked.map((one, index) => (
                <li key={index}>
                  <span>
                    {one.stem} {one.text}
                  </span>
                  {numbers ? <em>{numbers.values[index]}</em> : null}
                  <small>{one.feel}</small>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : view.question ? (
        <>
          <div className="cf-steps" aria-label={`${view.at + 1} 問目 / ${view.total} 問`}>
            {Array.from({ length: view.total }, (_, index) => (
              <i key={index} className={index < view.at ? "cf-done" : index === view.at ? "cf-now" : ""} />
            ))}
          </div>
          <p className="cf-stem">
            <small>
              {view.at + 1} / {view.total}
            </small>
            {view.question.stem}
          </p>
          <div className="cf-choices">
            {view.question.choices.map((text, index) => (
              <button key={`${view.at}-${index}`} type="button" className="cf-choice" onClick={() => onAnswer(index)} disabled={busy}>
                {text}
              </button>
            ))}
          </div>
          <p className="cf-feel">{feel}</p>
        </>
      ) : null}
    </>
  );
}
